import { Children, FC } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { Application } from '../../common/types';
import useApplications from '../../hooks/useApplications';
import { ProductStyles } from '../../common/sharedStyles';
import { Colors } from '../controls/helpers';
import { Product } from '../../api';

const GridLayout: FC<{ className?: string; classes?: Record<string, any>; isNarrowView?: boolean; }> = (props) => {
    const classes = useStyles(props);
    const { children, className, isNarrowView } = props;
    const gridBoxChildren = Children.map(children, child => child) ?? [];
    const lastChild = gridBoxChildren.pop();
    return <Box className={clsx(classes.root, className)}>
        <Row classes={{ row: classes.spacer }} isNarrowView={isNarrowView} />
        <Box className={classes.gridBox}>{gridBoxChildren}</Box>
        {lastChild}
    </Box>
}

interface RowProps {
    className?: string;
    classes?: Record<string, any>;
    isNarrowView?: boolean;
}

export const Row: FC<RowProps> = (props) => {
    const classes = useStyles(props);
    return <Box className={classes.row}>
        <Box className={clsx(props.className, classes.rowContent)}>{props.children}</Box>
    </Box>
}

export const Header: FC<RowProps> = (props) => {
    const classes = useStyles(props);
    return <Row classes={{ row: classes.header }} {...props} />
}

export const Body: FC<RowProps> = ({ classes: { root: rootClass } = {}, ...rest }) => {
    const classes = useStyles({ ...rest });
    return <Row classes={{ row: clsx(classes.body, rootClass) }} {...rest} />
}

export const Actions: FC<RowProps> = (props) => {
    const classes = useStyles(props);
    return <Row classes={{ row: classes.actions }} {...props} />
}

export const Hero: FC<RowProps & { product?: Product | 'NzFunds' }> = ({ product, children, ...props }) => {
    const classes = useStyles(props);
    let productTheme = product;

    if (!productTheme) {
        productTheme = Product.KiwiSaver;
        const applications = useApplications();
        if (!applications.includes(Application.KiwiSaver)) productTheme = applications.includes(Application.WealthBuilder) ? Product.WealthBuilder : Product.IncomeGenerator;
    }

    return <Row classes={{ row: clsx(classes.hero, classes[productTheme]) }} {...props}>{children}</Row>;
}

const useStyles = makeStyles({
    root: {
        flex: '1 0 auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
    },
    gridBox: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
        flex: '1 0 auto',
        '@media(min-width: 768px)': {
            flex: 0
        }
    },
    row: {
        display: 'flex',
        justifyContent: 'center',
        borderBottom: `1px solid ${Colors.Grey}`,
        '$root > &:last-child': {
            flex: '1 0 auto',
            borderBottom: 'none'
        },
        '@media(max-width: 767px)': {
            '&$header + &': {
                marginTop: 48
            }
        }
    },
    rowContent: ({ isNarrowView }: { isNarrowView?: boolean }) => ({
        width: '100%',
        '@media(min-width: 768px)': {
            width: isNarrowView === true ? 695 : 1028,
            borderLeft: `1px solid ${Colors.Grey}`,
            borderRight: `1px solid ${Colors.Grey}`
        }
    }),
    spacer: {
        '@media(min-width: 768px)': {
            flex: 1
        },
        '@media(max-width: 767px)': {
            borderBottom: 'none'
        }
    },
    header: {
        borderBottom: `1px solid ${Colors.Grey}`,
        '& $rowContent': {
            display: 'flex',
            justifyContent: 'space-between',
            backgroundColor: Colors.LightGrey,
            alignItems: 'center',
            padding: '6px 18px 6px 6px',
            minHeight: 45
        },
        '@media(max-width: 767px)': {
            position: 'fixed',
            width: '100%',
            top: 0,
            zIndex: 1000
        }
    },
    body: {
        flex: 1,
        '@media(min-width: 768px)': {
            flex: '1 0 auto'
        },
        '& $rowContent': {
            backgroundColor: Colors.LightGrey,
            padding: '18px 24px',
            display: 'flex',
            flexDirection: 'column',
            '@media(min-width: 768px)': {
                minHeight: 312,
                '$hero + &': {
                    minHeight: 290
                }
            },
            '& h4': {
                marginBottom: 60
            }
        }
    },
    actions: {
        '& $rowContent': {
            padding: 12,
            display: 'flex',
            minHeight: 64,
            alignItems: 'flex-start'
        },
        '@media(max-width: 767px)': {
            '&&&': {
                maxHeight: 64
            }
        }
    },
    hero: {
        '& $rowContent': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 248,
            '@media(min-width: 768px)': {
                height: 288
            }
        }
    },
    KiwiSaver: {
        '& $rowContent': {
            backgroundColor: ProductStyles.KiwiSaver.color
        }
    },
    WealthBuilder: {
        '& $rowContent': {
            backgroundColor: ProductStyles.WealthBuilder.color
        }
    },
    IncomeGenerator: {
        '& $rowContent': {
            backgroundColor: ProductStyles.IncomeGenerator.color
        }
    },
    NzFunds: {
        '& $rowContent': {
            backgroundColor: Colors.Blue
        }
    }
}, { name: 'GridLayout' });

export default GridLayout;
